import React, { useState, useEffect, useRef } from 'react'
import { ProjectWithComments, ProjectItem } from '../../../../types/project'
import Modal from '../../../Modal/Modal'
import { ItemModal, PROJECT_CATEGORIES } from './ItemModal'
import ItemCommentsModal from './ItemCommentsModal'
import { useRequests } from '../../../../requests/useRequests'
import { Package } from '../../../../enums/package.enum'
import { isMinimumPackage } from '../../../../utils/package-validator'
import { useSelector } from 'react-redux'
import { State } from '../../../../types/state'

interface ProjectStagesViewProps {
  project: ProjectWithComments
  onProjectUpdate: (updatedProject: ProjectWithComments) => void
  itemModalMode: 'add' | 'edit' | null
  itemModalData: ProjectItem | Omit<ProjectItem, 'id' | 'position'> | null
  setItemModalData: (data: ProjectItem | Omit<ProjectItem, 'id' | 'position'> | null) => void
  closeItemModal: () => void
  handleSaveItem: (item: any) => void
  handleApproveItem: (itemId: string) => Promise<void>
  handleRejectItem: (itemId: string) => Promise<void>
  handleChangeInternalItemStatus: (itemId: string, status: string) => Promise<void>
  openEditModal: (itemId: string) => void
  globalCategoryFilter: string
  globalStatusFilter: string[]
  onOpenImageModal: (itemId: string, imageUrl?: string) => void
  openAddItemModalForStage: (stageId: string) => void
  globalStageFilter?: string
}

const ProjectStagesView: React.FC<ProjectStagesViewProps> = ({
  project,
  onProjectUpdate,
  itemModalMode,
  itemModalData,
  setItemModalData,
  closeItemModal,
  handleSaveItem,
  handleChangeInternalItemStatus,
  openEditModal,
  globalCategoryFilter,
  globalStatusFilter,
  onOpenImageModal,
  globalStageFilter
}) => {
  const { package: userPackage, packageExpiredAt } = useSelector((state: State) => state.user)
  const requestMaker = useRequests()
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [showCommentsModal, setShowCommentsModal] = useState(false)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [selectedItemName, setSelectedItemName] = useState<string>('')
  const [hoveredImageId, setHoveredImageId] = useState<string | null>(null)
  const [editingStageId, setEditingStageId] = useState<string | null>(null)
  const [editingStageName, setEditingStageName] = useState('')
  const [showVisualizationModal, setShowVisualizationModal] = useState(false)
  const [showAttachmentModal, setShowAttachmentModal] = useState(false)
  const [editingNoteItemId, setEditingNoteItemId] = useState<string | null>(null)
  const [noteValue, setNoteValue] = useState('')
  const [editingField, setEditingField] = useState<{ itemId: string, field: string } | null>(null)
  const [editingFieldValue, setEditingFieldValue] = useState<string | number>('')
  const [showAddItemDropdown, setShowAddItemDropdown] = useState<string | null>(null)

  // Refs for dropdown menus
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  // Handle clicks outside dropdown menus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close main item dropdown menus
      if (openMenuId && !dropdownRefs.current[openMenuId]?.contains(event.target as Node)) {
        setOpenMenuId(null)
      }

      // Close add item dropdown menus
      if (showAddItemDropdown && !dropdownRefs.current[`add-${showAddItemDropdown}`]?.contains(event.target as Node)) {
        setShowAddItemDropdown(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openMenuId, showAddItemDropdown])

  const handleOpenComments = (itemId: string, itemName?: string) => {
    setSelectedItemId(itemId)
    setSelectedItemName(itemName || '')
    setShowCommentsModal(true)
  }

  const handleCloseComments = () => {
    setShowCommentsModal(false)
    setSelectedItemId(null)
    setSelectedItemName('')
  }

  const handleAddStageConfirm = async () => {
    try {
      const response = await requestMaker.addProjectStage(project.id, { name: '' })
      const updatedProject = await (await requestMaker.getProject(project.id)).data
      onProjectUpdate(updatedProject)

      const newStage = updatedProject.stages[updatedProject.stages.length - 1]
      if (newStage) {
        setEditingStageId(newStage.id)
        setEditingStageName(newStage.name)
      }
    } catch (e) {
      alert('Błąd podczas dodawania etapu')
    }
  }

  const handleEditStage = (stageId: string, stageName: string) => {
    setEditingStageId(stageId)
    setEditingStageName(stageName)
  }

  const handleEditStageConfirm = async () => {
    if (!editingStageId || editingStageName.trim() === '') {
      setEditingStageId(null)
      return
    }
    const previousStage = project.stages.find(s => s.id === editingStageId)
    if (previousStage == null) return

    if (previousStage.name === editingStageName.trim()) {
      setEditingStageId(null)
      setEditingStageName('')
      return
    }

    try {
      const updatedProject = {
        ...project,
        stages: project.stages.map(stage =>
          stage.id === editingStageId
            ? { ...stage, name: editingStageName }
            : stage
        )
      }
      onProjectUpdate(updatedProject)
      setEditingStageId(null)
      setEditingStageName('')
      await requestMaker.updateProjectStage(project.id, editingStageId, { name: editingStageName })
    } catch (e) {
      onProjectUpdate(project)
      alert('Błąd podczas edycji etapu')
    }
  }

  const handleEditStageKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      await handleEditStageConfirm()
    } else if (e.key === 'Escape') {
      if (!editingStageId) return

      const originalStage = project.stages.find(s => s.id === editingStageId)
      if ((originalStage != null) && originalStage.name.trim() === '') {
        await handleDeleteStage(editingStageId)
      }
      setEditingStageId(null)
      setEditingStageName('')
    }
  }

  const handleEditStageBlur = async () => {
    if (!editingStageId) return

    const originalStage = project.stages.find(s => s.id === editingStageId)
    if (originalStage == null) {
      setEditingStageId(null)
      return
    }
    const isNewStage = originalStage.name.trim() === ''
    const isNameEmpty = editingStageName.trim() === ''

    if (isNewStage && isNameEmpty) {
      setEditingStageId(null)
      await handleDeleteStage(editingStageId)
    } else {
      await handleEditStageConfirm()
    }
  }

  const handleDeleteStage = async (stageId: string) => {
    const stageToDelete = project.stages.find(s => s.id === stageId)
    if (stageToDelete == null) return

    const shouldConfirm = stageToDelete.name.trim() !== '' || project.items.some(item => item.stageId === stageId)

    if (!shouldConfirm || window.confirm('Czy na pewno chcesz usunąć ten etap? Wszystkie elementy w tym etapie zostaną również usunięte.')) {
      try {
        const updatedProject = {
          ...project,
          stages: project.stages.filter(stage => stage.id !== stageId),
          items: project.items.filter(item => item.stageId !== stageId)
        }
        onProjectUpdate(updatedProject)
        await requestMaker.deleteProjectStage(project.id, stageId)
      } catch (e) {
        onProjectUpdate(project)
        alert('Błąd podczas usuwania etapu')
      }
    }
  }

  const handleStartAddingItem = async (stageId: string, type: 'product' | 'visualization' | 'attachment' | 'note' | 'service') => {
    try {
      const itemToSave = {
        name: ' ',
        type,
        price: 0,
        quantity: 1,
        unit: 'szt.' as const,
        description: '',
        stageId,
        category: '',
        dimensions: '',
        position: project.items.filter(item => item.stageId === stageId).reduce((max, item) => Math.max(max, item.position), 0) + 1
      }

      const newItem = await requestMaker.addProjectItem(project.id, itemToSave)

      if (newItem.data) {
        const updatedProjectData = await requestMaker.getProject(project.id)
        if (updatedProjectData?.data) {
          onProjectUpdate(updatedProjectData.data)
          startEditingField(newItem.data.id, 'name', '')
        }
      }
    } catch (e) {
      alert('Błąd podczas dodawania elementu')
    }
  }

  const handleNoteClick = (itemId: string, currentNote: string) => {
    setEditingNoteItemId(itemId)
    setNoteValue(currentNote || '')
  }

  const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNoteValue(e.target.value)
  }

  const handleNoteKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>, itemId: string) => {
    if (e.key === 'Enter') {
      const itemToUpdate = project.items.find(i => i.id === itemId)
      if (itemToUpdate == null) return

      try {
        const updatedProject = {
          ...project,
          items: project.items.map(item =>
            item.id === itemId
              ? { ...item, note: noteValue }
              : item
          )
        }
        onProjectUpdate(updatedProject)
        setEditingNoteItemId(null)
        await requestMaker.updateProjectItem(project.id, itemId, { note: noteValue })
      } catch (error) {
        onProjectUpdate(project)
        console.error('Błąd podczas aktualizacji notatki:', error)
        setEditingNoteItemId(null)
      }
    } else if (e.key === 'Escape') {
      setEditingNoteItemId(null)
    }
  }

  const handleNoteBlur = () => {
    setEditingNoteItemId(null)
  }

  const handleOpenVisualization = (itemId: string, itemName?: string) => {
    setSelectedItemId(itemId)
    setSelectedItemName(itemName || '')
    setShowVisualizationModal(true)
  }

  const handleOpenAttachment = (itemId: string, itemName?: string) => {
    setSelectedItemId(itemId)
    setSelectedItemName(itemName || '')
    setShowAttachmentModal(true)
  }

  const handleCloseVisualization = () => {
    setShowVisualizationModal(false)
    setSelectedItemId(null)
    setSelectedItemName('')
  }

  const handleCloseAttachment = () => {
    setShowAttachmentModal(false)
    setSelectedItemId(null)
    setSelectedItemName('')
  }

  const handleSaveVisualization = async (file: File) => {
    if (!selectedItemId) return

    try {
      const uploadResponse = await requestMaker.uploadFile(file, 'project')
      await requestMaker.updateProjectItemVisualization(project.id, selectedItemId, uploadResponse.data.fileKey)
      const updatedProject = await requestMaker.getProject(project.id)
      if (updatedProject && updatedProject.data) {
        onProjectUpdate(updatedProject.data)
      }
      handleCloseVisualization()
    } catch (error) {
      console.error('Błąd podczas dodawania wizualizacji:', error)
    }
  }

  const handleSaveAttachment = async (file: File) => {
    if (!selectedItemId) return

    try {
      const uploadResponse = await requestMaker.uploadFile(file, 'project')
      await requestMaker.updateProjectItemAttachment(project.id, selectedItemId, uploadResponse.data.fileKey)
      const updatedProject = await requestMaker.getProject(project.id)
      if (updatedProject && updatedProject.data) {
        onProjectUpdate(updatedProject.data)
      }
      handleCloseAttachment()
    } catch (error) {
      console.error('Błąd podczas dodawania załącznika:', error)
    }
  }

  const handleDeleteVisualization = async () => {
    if (!selectedItemId) return

    try {
      const updatedProject = {
        ...project,
        items: project.items.map(item =>
          item.id === selectedItemId
            ? { ...item, visualizationUrl: undefined }
            : item
        )
      }
      onProjectUpdate(updatedProject)
      await requestMaker.deleteProjectItemVisualization(project.id, selectedItemId)
      handleCloseVisualization()
    } catch (error) {
      onProjectUpdate(project)
      console.error('Błąd podczas usuwania wizualizacji:', error)
    }
  }

  const handleDeleteAttachment = async () => {
    if (!selectedItemId) return

    try {
      const updatedProject = {
        ...project,
        items: project.items.map(item =>
          item.id === selectedItemId
            ? { ...item, attachmentUrl: undefined }
            : item
        )
      }
      onProjectUpdate(updatedProject)
      await requestMaker.deleteProjectItemAttachment(project.id, selectedItemId)
      handleCloseAttachment()
    } catch (error) {
      onProjectUpdate(project)
      console.error('Błąd podczas usuwania załącznika:', error)
    }
  }

  const startEditingField = (itemId: string, field: string, value: string | number) => {
    setEditingField({ itemId, field })
    setEditingFieldValue(value)
  }

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setEditingFieldValue(e.target.value)
  }

  const saveEditingField = async () => {
    if (editingField == null) return

    try {
      let value: any = editingFieldValue
      if (editingField.field === 'price' || editingField.field === 'quantity') {
        value = Number(editingFieldValue)
        if (isNaN(value)) value = 0
      }

      const updatedProject = {
        ...project,
        items: project.items.map(item =>
          Number(item.id) === Number(editingField.itemId)
            ? { ...item, [editingField.field]: value }
            : item
        )
      }
      onProjectUpdate(updatedProject)
      setEditingField(null)
      await requestMaker.updateProjectItem(project.id, editingField.itemId, { [editingField.field]: value })
    } catch (error) {
      onProjectUpdate(project)
      console.error('Błąd podczas aktualizacji pola:', error)
      setEditingField(null)
    }
  }

  const handleFieldKeyDown = async (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.key === 'Enter') {
      await saveEditingField()
    } else if (e.key === 'Escape') {
      setEditingField(null)
    }
  }

  const handleFieldBlur = async () => {
    await saveEditingField()
  }

  const handleDeleteItem = async (itemId: string) => {
    const updatedProject = {
      ...project,
      items: project.items.filter(item => item.id !== itemId)
    }
    onProjectUpdate(updatedProject)
    requestMaker.deleteProjectItem(project.id, itemId).catch(() => {
      onProjectUpdate(project)
      alert('Nie udało się usunąć elementu')
    })
  }

  const handleCopyItem = async (itemId: string) => {
    try {
      await requestMaker.copyProjectItem(project.id, itemId)
      const updatedProject = await (await requestMaker.getProject(project.id)).data
      onProjectUpdate(updatedProject)
    } catch (e) {
      alert('Nie udało się skopiować elementu')
    }
  }

  const handleMoveItemUp = async (itemId: string, stageId: string) => {
    const stageItems = project.items.filter(item => item.stageId === stageId).sort((a, b) => a.position - b.position)
    const currentIndex = stageItems.findIndex(item => item.id === itemId)
    if (currentIndex <= 0) return
    const updates = [
      { id: stageItems[currentIndex].id, position: stageItems[currentIndex - 1].position, stageId },
      { id: stageItems[currentIndex - 1].id, position: stageItems[currentIndex].position, stageId }
    ]
    let items = project.items.map(item => {
      const update = updates.find(u => u.id === item.id)
      return (update != null) ? { ...item, position: update.position } : item
    })
    items = [
      ...items.filter(item => item.stageId !== stageId),
      ...items.filter(item => item.stageId === stageId).sort((a, b) => a.position - b.position)
    ]
    onProjectUpdate({ ...project, items })
    requestMaker.reorderProjectItems(project.id, updates).catch(() => {
      onProjectUpdate(project)
      alert('Nie udało się przenieść elementu')
    })
  }

  const handleMoveItemDown = async (itemId: string, stageId: string) => {
    const stageItems = project.items.filter(item => item.stageId === stageId).sort((a, b) => a.position - b.position)
    const currentIndex = stageItems.findIndex(item => item.id === itemId)
    if (currentIndex === -1 || currentIndex >= stageItems.length - 1) return
    const updates = [
      { id: stageItems[currentIndex].id, position: stageItems[currentIndex + 1].position, stageId },
      { id: stageItems[currentIndex + 1].id, position: stageItems[currentIndex].position, stageId }
    ]
    let items = project.items.map(item => {
      const update = updates.find(u => u.id === item.id)
      return (update != null) ? { ...item, position: update.position } : item
    })
    items = [
      ...items.filter(item => item.stageId !== stageId),
      ...items.filter(item => item.stageId === stageId).sort((a, b) => a.position - b.position)
    ]
    onProjectUpdate({ ...project, items })
    requestMaker.reorderProjectItems(project.id, updates).catch(() => {
      onProjectUpdate(project)
      alert('Nie udało się przenieść elementu')
    })
  }

  const handleToggleVisibility = async (itemId: string, isHidden: boolean) => {
    const updatedProject = {
      ...project,
      items: project.items.map(item =>
        item.id === itemId ? { ...item, isHidden } : item
      )
    }
    onProjectUpdate(updatedProject)
    try {
      await requestMaker.updateProjectItemVisibility(project.id, itemId, isHidden)
    } catch (error) {
      onProjectUpdate(project)
      alert('Nie udało się zmienić widoczności elementu')
    }
  }

  const handleAddItemOption = async (stageId: string, type: 'product' | 'visualization' | 'attachment' | 'note' | 'service') => {
    handleStartAddingItem(stageId, type)
  }

  const handleChangeInternalStatus = async (itemId: string, status: 'none' | 'in_valuation' | 'ordered') => {
    try {
      const updatedProject = {
        ...project,
        items: project.items.map(item =>
          item.id === itemId
            ? { ...item, internalStatus: status }
            : item
        )
      }
      onProjectUpdate(updatedProject)
      await requestMaker.updateProjectItem(project.id, itemId, { internalStatus: status })
    } catch (error) {
      onProjectUpdate(project)
      console.error('Błąd podczas aktualizacji statusu:', error)
    }
  }

  return (
    <div className='stages-view tw-py-4 md:tw-py-6'>
      {project.stages
        .filter(stage => !globalStageFilter || stage.name === globalStageFilter)
        .map((stage, stageIdx) => {
          const isEditing = editingStageId === stage.id

          const stageColors = [
            '#4F8A8B', '#F9ED69', '#F08A5D', '#B83B5E', '#6A2C70',
            '#3A6351', '#F7B801', '#EA5455', '#2D4059', '#FF6F3C'
          ]
          const color = stageColors[stageIdx % stageColors.length]

          return (
            <div key={stage.id} className='tw-mb-6'>
              <div
                className='tw-border-l-4 tw-pl-4'
                style={{ borderColor: color }}
              >
                <div className='tw-group tw-flex tw-items-center tw-gap-3'>
                  {isEditing
                    ? (
                      <input
                        type='text'
                        className='tw-flex-grow tw-text-xl tw-font-bold tw-py-1 tw-bg-transparent focus:tw-outline-none'
                        value={editingStageName}
                        onChange={(e) => setEditingStageName(e.target.value)}
                        onKeyDown={handleEditStageKeyDown}
                        onBlur={handleEditStageBlur}
                        placeholder='Wprowadź nazwę sekcji...'
                        autoFocus
                      />
                      )
                    : (
                      <h5
                        className='tw-text-xl tw-font-bold tw-text-gray-800 tw-cursor-pointer'
                        onClick={() => handleEditStage(stage.id, stage.name)}
                      >
                        {stage.name ? stage.name : <span className='tw-text-gray-400'>Kliknij aby dodać nazwę</span>}
                      </h5>
                      )}
                  <button
                    className='tw-text-red-500 hover:tw-text-red-700 focus:tw-text-red-700 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity'
                    onClick={async () => await handleDeleteStage(stage.id)}
                    title='Usuń etap'
                  >
                    <i className='fa fa-trash tw-text-lg' />
                  </button>
                </div>

                <div className='tw-border-b tw-border-dotted tw-border-gray-300 tw-my-4' />

                <div className='tw-flex-1 tw-flex tw-flex-col tw-gap-3'>
                  {project.items
                    .filter(item => item.stageId === stage.id)
                    .map(item => (
                      <div
                        key={item.id}
                        className='stage-item-row tw-bg-white tw-rounded-lg tw-shadow-md tw-flex tw-flex-row tw-items-stretch'
                      >
                        <div className='tw-flex-1 tw-flex tw-flex-col md:tw-flex-row md:tw-items-start md:tw-gap-x-4 lg:tw-gap-x-6 tw-gap-y-4 tw-p-3 md:tw-p-4'>
                            <div className='tw-flex tw-items-start tw-gap-x-3 tw-w-full md:tw-flex-1 md:tw-min-w-0'>
                                {isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && <div className='tw-flex-shrink-0 tw-text-center tw-min-w-[28px] tw-pt-1'>
                                  <div className='tw-flex tw-flex-col tw-gap-2'>
                                      <button
                                        type='button'
                                        className='tw-text-blue-500 hover:tw-text-blue-600 focus:tw-outline-none'
                                        onClick={async () => await handleToggleVisibility(item.id, !item.isHidden)}
                                        title={item.isHidden ? 'Pokaż element' : 'Ukryj element'}
                                      >
                                        <i className={`fas fa-eye${item.isHidden ? '-slash' : ''} tw-text-base md:tw-text-lg`} />
                                      </button>
                                      {['product', 'visualization'].includes(item.type) && <button
                                        type='button'
                                        className='tw-text-blue-500 hover:tw-text-blue-600 focus:tw-outline-none'
                                        onClick={() => handleOpenVisualization(item.id, item.name)}
                                        title={item.visualizationUrl ? 'Edytuj wizualizację' : 'Dodaj wizualizację'}
                                                                                           >
                                        <i className={`fas fa-paint-brush tw-text-sm md:tw-text-base ${item.visualizationUrl ? 'tw-text-green-500' : ''}`} />
                                                                                           </button>}
                                      {['attachment', 'product'].includes(item.type) && isMinimumPackage(userPackage, Package.PREMIUM, packageExpiredAt) && <button
                                        type='button'
                                        className='tw-text-blue-500 hover:tw-text-blue-600 focus:tw-outline-none'
                                        onClick={() => handleOpenAttachment(item.id, item.name)}
                                        title={item.attachmentUrl ? 'Edytuj załącznik' : 'Dodaj załącznik'}
                                                                                                                                                            >
                                        <i className={`fas fa-paperclip tw-text-sm md:tw-text-base ${item.attachmentUrl ? 'tw-text-green-500' : ''}`} />
                                                                                                                                                            </button>}
                                    </div>
                                                                                                  </div>}

                                <div className='tw-flex-shrink-0'>
                                  {['product', 'visualization'].includes(item.type) && <div
                                      className='tw-relative tw-inline-block'
                                      onMouseEnter={() => setHoveredImageId(item.id)}
                                      onMouseLeave={() => setHoveredImageId(null)}
                                                                                         >
                                      {item.imageUrl
                                        ? (
                                          <>
                                            <img
                                                src={item.imageUrl}
                                                alt={item.name}
                                                className='tw-w-[100px] tw-h-[100px] md:tw-w-[120px] md:tw-h-[120px] tw-object-cover tw-rounded'
                                              />
                                            <div
                                                className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-60 tw-transition-opacity tw-duration-200 tw-cursor-pointer tw-rounded tw-z-10'
                                                style={{ opacity: hoveredImageId === item.id ? 1 : 0 }}
                                                onClick={() => onOpenImageModal(item.id, item.imageUrl)}
                                              >
                                                <i className='fas fa-plus tw-text-white tw-text-lg md:tw-text-xl' />
                                              </div>
                                          </>
                                          )
                                        : (
                                          <div
                                            className='tw-w-[100px] tw-h-[100px] md:tw-w-[120px] md:tw-h-[120px] tw-rounded tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-relative tw-cursor-pointer'
                                            onClick={() => onOpenImageModal(item.id)}
                                            onMouseEnter={() => setHoveredImageId(item.id)}
                                            onMouseLeave={() => setHoveredImageId(null)}
                                          >
                                            <i className='fas fa-image tw-text-2xl md:tw-text-3xl tw-text-gray-300' />
                                            <div
                                                className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-60 tw-transition-opacity tw-duration-200 tw-rounded tw-z-10'
                                                style={{ opacity: hoveredImageId === item.id ? 1 : 0 }}
                                              >
                                                <i className='fas fa-plus tw-text-white tw-text-lg md:tw-text-xl' />
                                              </div>
                                          </div>
                                          )}
                                    </div>}
                                </div>

                                <div className='tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-gap-1'>
                                  <div className='tw-flex tw-items-center tw-flex-wrap tw-gap-x-2 tw-gap-y-1'>
                                      {Number(editingField?.itemId) === Number(item.id) && editingField?.field === 'name'
                                        ? (
                                          <input
                                            type='text'
                                            className='tw-w-full tw-px-1 tw-py-0.5 tw-border tw-border-gray-300 tw-rounded tw-font-semibold tw-text-sm md:tw-text-base tw-text-gray-800'
                                            value={editingFieldValue as string}
                                            onChange={handleFieldChange}
                                            onBlur={handleFieldBlur}
                                            onKeyDown={handleFieldKeyDown}
                                            autoFocus
                                          />
                                          )
                                        : (
                                          <div className='tw-flex tw-items-center tw-gap-2'>
                                            {project.isShared && (
                                                <div className='tw-flex tw-items-center tw-gap-1'>
                                                    {item.status === 'approved' && <i className='fa fa-check-circle tw-text-green-700 tw-text-xl' />}
                                                    {item.status === 'rejected' && <i className='fa fa-times-circle tw-text-red-700 tw-text-xl' />}
                                                    {item.status === 'pending' && <i className='fa fa-question-circle tw-text-yellow-500 tw-text-xl' />}
                                                  </div>
                                              )}
                                            <strong
                                                className='tw-font-semibold tw-text-sm md:tw-text-base tw-text-gray-800 tw-mr-1 tw-cursor-pointer hover:tw-underline'
                                                onClick={() => startEditingField(item.id, 'name', item.name || '')}
                                              >
                                                {!item.name ? <span className='tw-text-gray-400'>Kliknij aby dodać nazwę</span> : item.name}
                                              </strong>
                                          </div>
                                          )}
                                      {item.url && (
                                        <a href={item.url} target='_blank' rel='noopener noreferrer' className='tw-text-blue-500 hover:tw-text-blue-600' title='Zobacz stronę'>
                                          <i className='fas fa-link tw-text-xs' />
                                        </a>
                                      )}
                                      {isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && <span className='tw-ml-2'>
                                        <div className='tw-relative tw-inline-block'>
                                          <button
                                            type='button'
                                            className='tw-flex tw-items-center tw-gap-1 tw-cursor-pointer hover:tw-opacity-80'
                                            onClick={() => setOpenMenuId(openMenuId === `status-${item.id}` ? null : `status-${item.id}`)}
                                          >
                                            {item.internalStatus === 'none' &&
                                                <span className='tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-yellow-100 tw-text-gray-700'>Bez statusu</span>}
                                            {item.internalStatus === 'in_valuation' &&
                                                <span className='tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-blue-100 tw-text-blue-700'>W wycenie</span>}
                                            {item.internalStatus === 'ordered' &&
                                                <span className='tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-green-100 tw-text-green-700'>Zamówione</span>}
                                          </button>
                                          {openMenuId === `status-${item.id}` && (
                                            <div
                                                ref={(el) => dropdownRefs.current[`status-${item.id}`] = el}
                                                className='tw-absolute tw-right-0 tw-mt-2 tw-w-48 tw-origin-top-right tw-bg-white tw-rounded-md tw-shadow-xl tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none tw-z-50'
                                              >
                                                <div className='tw-py-1' role='menu' aria-orientation='vertical' aria-labelledby='status-menu'>
                                                    <button
                                                      type='button'
                                                      className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900'
                                                      onClick={() => { handleChangeInternalStatus(item.id, 'none'); setOpenMenuId(null) }}
                                                    >
                                                      <span className='tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-gray-100 tw-text-gray-700'>Bez statusu</span>
                                                    </button>
                                                    <button
                                                      type='button'
                                                      className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900'
                                                      onClick={() => { handleChangeInternalStatus(item.id, 'in_valuation'); setOpenMenuId(null) }}
                                                    >
                                                      <span className='tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-blue-100 tw-text-blue-700'>W wycenie</span>
                                                    </button>
                                                    <button
                                                      type='button'
                                                      className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900'
                                                      onClick={() => { handleChangeInternalStatus(item.id, 'ordered'); setOpenMenuId(null) }}
                                                    >
                                                      <span className='tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-green-100 tw-text-green-700'>Zamówione</span>
                                                    </button>
                                                  </div>
                                              </div>
                                          )}
                                        </div>
                                                                                                        </span>}
                                    </div>
                                  {['note', 'product'].includes(item.type) && <div className='tw-text-gray-500 tw-text-xs md:tw-text-sm'>
                                      {editingNoteItemId === item.id
                                        ? (
                                          <input
                                            type='text'
                                            className='tw-w-full tw-px-2 tw-py-1 tw-text-xs tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-1 focus:tw-ring-indigo-500 focus:tw-border-indigo-500'
                                            value={noteValue}
                                            onChange={handleNoteChange}
                                            onKeyDown={async (e) => await handleNoteKeyDown(e, item.id)}
                                            onBlur={handleNoteBlur}
                                            autoFocus
                                          />
                                          )
                                        : (
                                          <div onClick={() => handleNoteClick(item.id, item.note || '')} className='tw-cursor-pointer hover:tw-text-gray-700'>
                                            {item.note || <span className='tw-text-gray-400'>Kliknij aby dodać notatkę</span>}
                                          </div>
                                          )}
                                    </div>}
                                  <div className='tw-flex tw-items-center'>
                                      <button type='button' className='tw-text-blue-500 hover:tw-text-blue-600 focus:tw-outline-none' onClick={() => handleOpenComments(item.id, item.name)} title='Pokaż komentarze'>
                                        <i className='fas fa-comment tw-text-sm md:tw-text-base' />
                                      </button>
                                    </div>
                                </div>
                              </div>

                            {['product'].includes(item.type) && <div className='tw-w-full md:tw-w-auto md:tw-max-w-md lg:tw-max-w-sm md:tw-min-w-[300px] lg:tw-min-w-[340px] md:tw-flex-shrink-0 tw-mt-3 md:tw-mt-0'>
                                <div className='tw-text-xs md:tw-text-sm tw-text-gray-600'>
                                  <div className='tw-flex tw-flex-col sm:tw-flex-row sm:tw-justify-between tw-gap-x-4 md:tw-gap-x-6 tw-gap-y-1'>
                                      <div className='tw-w-full sm:tw-flex-1 sm:tw-min-w-0'>
                                        <div>
                                          <span className='tw-font-semibold tw-text-gray-700'>Dostawca:</span>{' '}
                                          {editingField?.itemId === item.id && editingField.field === 'supplier'
                                            ? (
                                                <input
                                                    type='text'
                                                    className='tw-px-1 tw-py-0.5 tw-border tw-border-gray-300 tw-rounded'
                                                    value={editingFieldValue as string}
                                                    onChange={handleFieldChange}
                                                    onBlur={handleFieldBlur}
                                                    onKeyDown={handleFieldKeyDown}
                                                    autoFocus
                                                    style={{ width: 90 }}
                                                  />
                                              )
                                            : (
                                                <span className='tw-cursor-pointer hover:tw-underline' onClick={() => startEditingField(item.id, 'supplier', item.supplier || '')}>
                                                    {item.supplier || <span className='tw-text-gray-400'>-</span>}
                                                  </span>
                                              )}
                                        </div>
                                        <div>
                                          <span className='tw-font-semibold tw-text-gray-700'>Nr kat.:</span>{' '}
                                          {editingField?.itemId === item.id && editingField.field === 'catalogNumber'
                                            ? (
                                                <input
                                                    type='text'
                                                    className='tw-px-1 tw-py-0.5 tw-border tw-border-gray-300 tw-rounded'
                                                    value={editingFieldValue as string}
                                                    onChange={handleFieldChange}
                                                    onBlur={handleFieldBlur}
                                                    onKeyDown={handleFieldKeyDown}
                                                    autoFocus
                                                    style={{ width: 90 }}
                                                  />
                                              )
                                            : (
                                                <span className='tw-cursor-pointer hover:tw-underline' onClick={() => startEditingField(item.id, 'catalogNumber', item.catalogNumber || '')}>
                                                    {item.catalogNumber || <span className='tw-text-gray-400'>-</span>}
                                                  </span>
                                              )}
                                        </div>
                                        <div>
                                          <span className='tw-font-semibold tw-text-gray-700'>Kategoria:</span>{' '}
                                          {editingField?.itemId === item.id && editingField.field === 'category'
                                            ? (
                                                <select
                                                    className='tw-px-1 tw-py-0.5 tw-border tw-border-gray-300 tw-rounded'
                                                    value={editingFieldValue as string}
                                                    onChange={handleFieldChange}
                                                    onBlur={handleFieldBlur}
                                                    onKeyDown={handleFieldKeyDown}
                                                    autoFocus
                                                    style={{ width: 110 }}
                                                  >
                                                    <option value=''>Wybierz...</option>
                                                    {PROJECT_CATEGORIES.map(category => (
                                                      <option key={category.id} value={category.id}>{category.label}</option>
                                                    ))}
                                                  </select>
                                              )
                                            : (
                                                <span className='tw-cursor-pointer hover:tw-underline' onClick={() => startEditingField(item.id, 'category', item.category || '')}>
                                                    {PROJECT_CATEGORIES.find(c => c.id === item.category)?.label || <span className='tw-text-gray-400'>-</span>}
                                                  </span>
                                              )}
                                        </div>
                                        <div>
                                          <span className='tw-font-semibold tw-text-gray-700'>Wymiar:</span>{' '}
                                          {editingField?.itemId === item.id && editingField.field === 'dimensions'
                                            ? (
                                                <input
                                                    type='text'
                                                    className='tw-px-1 tw-py-0.5 tw-border tw-border-gray-300 tw-rounded'
                                                    value={editingFieldValue as string}
                                                    onChange={handleFieldChange}
                                                    onBlur={handleFieldBlur}
                                                    onKeyDown={handleFieldKeyDown}
                                                    autoFocus
                                                    style={{ width: 90 }}
                                                  />
                                              )
                                            : (
                                                <span className='tw-cursor-pointer hover:tw-underline' onClick={() => startEditingField(item.id, 'dimensions', item.dimensions || '')}>
                                                    {item.dimensions || <span className='tw-text-gray-400'>-</span>}
                                                  </span>
                                              )}
                                        </div>
                                      </div>
                                      <div className='tw-w-full sm:tw-flex-1 sm:tw-min-w-0 sm:tw-text-left md:tw-text-right'>
                                        <div>
                                          <span className='tw-font-semibold tw-text-gray-700'>{item.unit}:</span>{' '}
                                          {editingField?.itemId === item.id && editingField.field === 'quantity'
                                            ? (
                                                <input
                                                    type='number'
                                                    className='tw-px-1 tw-py-0.5 tw-border tw-border-gray-300 tw-rounded'
                                                    value={editingFieldValue as number}
                                                    onChange={handleFieldChange}
                                                    onBlur={handleFieldBlur}
                                                    onKeyDown={handleFieldKeyDown}
                                                    autoFocus
                                                    min={1}
                                                    style={{ width: 60 }}
                                                  />
                                              )
                                            : (
                                                <span className='tw-cursor-pointer hover:tw-underline' onClick={() => startEditingField(item.id, 'quantity', item.quantity)}>
                                                    {item.quantity}
                                                  </span>
                                              )}
                                        </div>
                                        <div>
                                          <span className='tw-font-semibold tw-text-gray-700'>Cena jedn.:</span>{' '}
                                          {editingField?.itemId === item.id && editingField.field === 'price'
                                            ? (
                                                <input
                                                    type='number'
                                                    className='tw-px-1 tw-py-0.5 tw-border tw-border-gray-300 tw-rounded'
                                                    value={editingFieldValue as number}
                                                    onChange={handleFieldChange}
                                                    onBlur={handleFieldBlur}
                                                    onKeyDown={handleFieldKeyDown}
                                                    autoFocus
                                                    min={0}
                                                    step={0.01}
                                                    style={{ width: 80 }}
                                                  />
                                              )
                                            : (
                                                <span className='tw-cursor-pointer hover:tw-underline' onClick={() => startEditingField(item.id, 'price', item.price)}>
                                                    {item.price?.toFixed(2)} zł
                                                  </span>
                                              )}
                                        </div>
                                        <div><span className='tw-font-semibold tw-text-gray-700'>Wartość:</span> {(() => {
                                          const basePrice = item.price * item.quantity
                                          const withDelivery = basePrice + (item.deliveryCost || 0)
                                          const withMargin = withDelivery * (1 + ((item.marginPercentage || 0) / 100))
                                          const withVat = withMargin * (1 + ((item.vatPercentage || 0) / 100))
                                          const withDiscount = withVat * (1 - ((item.discountPercentage || 0) / 100))
                                          return withDiscount.toFixed(2)
                                        })()} zł
                                        </div>
                                      </div>
                                    </div>
                                </div>
                              </div>}

                            <div className='tw-w-full md:tw-w-auto tw-flex-shrink-0 tw-text-right tw-min-w-[30px] tw-mt-3 md:tw-mt-0 md:tw-ml-auto'>
                                <div className='tw-relative tw-inline-block'>
                                  <button
                                      type='button'
                                      className='tw-text-gray-500 hover:tw-text-gray-700 focus:tw-outline-none'
                                      onClick={() => setOpenMenuId(openMenuId === item.id ? null : item.id)}
                                    >
                                      <i className='fas fa-ellipsis-v tw-text-base md:tw-text-lg' />
                                    </button>
                                  {openMenuId === item.id && (
                                      <div
                                        ref={(el) => dropdownRefs.current[item.id] = el}
                                        className='tw-absolute tw-right-0 tw-mt-2 tw-w-56 tw-origin-top-right tw-bg-white tw-rounded-md tw-shadow-xl tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none tw-z-50'
                                      >
                                        <div className='tw-py-1' role='menu' aria-orientation='vertical' aria-labelledby='options-menu'>
                                          <button type='button' className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900' onClick={() => { openEditModal(item.id); setOpenMenuId(null) }}>
                                            <i className='fas fa-edit tw-mr-3 tw-text-gray-400' /> Edytuj
                                          </button>
                                          {isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && <button type='button' className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900' onClick={() => { handleCopyItem(item.id); setOpenMenuId(null) }}>
                                            <i className='fas fa-copy tw-mr-3 tw-text-gray-400' /> Kopiuj
                                                                                                            </button>}
                                          {isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && <button type='button' className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900' onClick={() => { handleMoveItemUp(item.id, item.stageId); setOpenMenuId(null) }}>
                                            <i className='fas fa-arrow-up tw-mr-3 tw-text-gray-400' /> Przesuń w górę
                                                                                                            </button>}
                                          {isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && <button type='button' className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900' onClick={() => { handleMoveItemDown(item.id, item.stageId); setOpenMenuId(null) }}>
                                            <i className='fas fa-arrow-down tw-mr-3 tw-text-gray-400' /> Przesuń w dół
                                                                                                            </button>}
                                          {isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && <button type='button' className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 hover:tw-bg-red-50 hover:tw-text-red-700' onClick={() => { handleDeleteItem(item.id); setOpenMenuId(null) }}>
                                            <i className='fas fa-trash tw-mr-3 tw-text-red-400' /> Usuń
                                                                                                            </button>}
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                          </div>
                      </div>
                    ))}
                </div>

                <div className='tw-mt-4'>
                  <div className='tw-relative tw-inline-block'>
                    <button
                      className='tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-blue-600 hover:tw-text-blue-800 tw-font-semibold'
                      onClick={() => setShowAddItemDropdown(showAddItemDropdown === stage.id ? null : stage.id)}
                    >
                      <i className='fa fa-plus' />
                      <span>Dodaj element do sekcji</span>
                    </button>
                    {showAddItemDropdown === stage.id && (
                      <div
                        ref={(el) => dropdownRefs.current[`add-${stage.id}`] = el}
                        className='tw-absolute tw-left-0 tw-mt-2 tw-w-56 tw-origin-top-left tw-bg-white tw-rounded-md tw-shadow-xl tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none tw-z-50'
                      >
                        <div className='tw-py-1' role='menu' aria-orientation='vertical' aria-labelledby='options-menu'>
                            <button
                                type='button'
                                className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900'
                                onClick={() => { setShowAddItemDropdown(null); handleAddItemOption(stage.id, 'product') }}
                              >
                                <i className='fas fa-box tw-mr-3 tw-text-gray-400' /> Produkt
                              </button>
                            <button
                                type='button'
                                className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900'
                                onClick={() => { setShowAddItemDropdown(null); handleAddItemOption(stage.id, 'service') }}
                              >
                                <i className='fas fa-concierge-bell tw-mr-3 tw-text-gray-400' /> Usługa
                              </button>
                            <button
                                type='button'
                                className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900'
                                onClick={() => { setShowAddItemDropdown(null); handleAddItemOption(stage.id, 'visualization') }}
                              >
                                <i className='fas fa-paint-brush tw-mr-3 tw-text-gray-400' /> Wizualizacja
                              </button>
                            <button
                                type='button'
                                className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900'
                                onClick={() => { setShowAddItemDropdown(null); handleAddItemOption(stage.id, 'attachment') }}
                              >
                                <i className='fas fa-paperclip tw-mr-3 tw-text-gray-400' /> Załącznik
                              </button>
                            <button
                                type='button'
                                className='tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900'
                                onClick={() => { setShowAddItemDropdown(null); handleAddItemOption(stage.id, 'note') }}
                              >
                                <i className='fas fa-sticky-note tw-mr-3 tw-text-gray-400' /> Notatka
                              </button>
                          </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}

      <div
        className='tw-flex tw-items-center tw-gap-3 tw-mt-8 tw-cursor-pointer'
        onClick={async () => await handleAddStageConfirm()}
        title='Dodaj sekcję'
      >
        <div className='tw-flex-shrink-0'>
          <div className='tw-w-6 tw-h-6 tw-bg-blue-500 group-hover:tw-bg-blue-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-colors'>
            <i className='fa fa-plus tw-text-white tw-text-xs' />
          </div>
        </div>
        <span className='tw-text-blue-600 tw-font-semibold tw-text-sm'>
          Dodaj sekcję
        </span>
        <div className='tw-flex-grow tw-border-t tw-border-gray-300 tw-ml-1' />
      </div>

      {showCommentsModal && selectedItemId && (
        <ItemCommentsModal
          show={showCommentsModal}
          onClose={handleCloseComments}
          projectId={project.id}
          itemId={selectedItemId}
          itemName={selectedItemName}
        />
      )}
      {itemModalMode && (itemModalData != null) && (
        <ItemModal
          isOpen
          onClose={closeItemModal}
          onSave={() => handleSaveItem(itemModalData)}
          mode={itemModalMode}
          itemData={itemModalData}
          setItemData={setItemModalData}
          project={project}
        />
      )}
      {showVisualizationModal && selectedItemId && (
        <Modal show={showVisualizationModal} onClose={handleCloseVisualization} title={`${selectedItemName ? `Wizualizacja: ${selectedItemName}` : 'Wizualizacja'}`}>
          <div className='tw-p-4'>
            <div className='tw-mb-4'>
              <input
                type='file'
                accept='image/*'
                onChange={async (e) => await (((e.target.files?.[0]) != null) && handleSaveVisualization(e.target.files[0]))}
                className='tw-w-full'
              />
            </div>
            {project.items.find(i => i.id === selectedItemId)?.visualizationUrl && (
              <div className='tw-mb-4 tw-flex tw-justify-center'>
                <img
                  src={project.items.find(i => i.id === selectedItemId)?.visualizationUrl}
                  alt='Wizualizacja'
                  style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, boxShadow: '0 2px 8px #0001' }}
                />
              </div>
            )}
            {project.items.find(i => i.id === selectedItemId)?.visualizationUrl && (
              <div className='tw-mt-4'>
                <button
                  type='button'
                  className='tw-text-red-500 hover:tw-text-red-600 focus:tw-outline-none'
                  onClick={handleDeleteVisualization}
                >
                  <i className='fas fa-trash tw-mr-2' />
                  Usuń wizualizację
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
      {showAttachmentModal && selectedItemId && (
        <Modal show={showAttachmentModal} onClose={handleCloseAttachment} title={`${selectedItemName ? `Załącznik: ${selectedItemName}` : 'Załącznik'}`}>
          <div className='tw-p-4'>
            <div className='tw-mb-4'>
              <input
                type='file'
                onChange={async (e) => await (((e.target.files?.[0]) != null) && handleSaveAttachment(e.target.files[0]))}
                className='tw-w-full'
              />
            </div>
            {project.items.find(i => i.id === selectedItemId)?.attachmentUrl && (
              <div className='tw-mb-4 tw-flex tw-justify-center'>
                <a
                  href={project.items.find(i => i.id === selectedItemId)?.attachmentUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='tw-text-blue-600 hover:tw-underline tw-break-all'
                >
                  Podgląd załącznika
                </a>
              </div>
            )}
            {project.items.find(i => i.id === selectedItemId)?.attachmentUrl && (
              <div className='tw-mt-4'>
                <button
                  type='button'
                  className='tw-text-red-500 hover:tw-text-red-600 focus:tw-outline-none'
                  onClick={handleDeleteAttachment}
                >
                  <i className='fas fa-trash tw-mr-2' />
                  Usuń załącznik
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}

export default ProjectStagesView
